// frontend/src/components/dashboard/RecentActivityCard.tsx

import React from 'react';
import { Bot, CheckCircle2, Trophy, BookOpen, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ActivityLogItem, ActivityType } from '../../types/dashboard.types';

const ACTIVITY_META: Record<ActivityType, { icon: React.ElementType; bg: string; label: string }> = {
  lesson:      { icon: BookOpen,      bg: '#17C79A', label: 'Ders Tamamlandı' },
  note:        { icon: FileText,      bg: '#06B6D4', label: 'Not Alındı' },
  ai_chat:     { icon: Bot,           bg: '#3B82F6', label: 'AI Mentor' },
  quiz:        { icon: CheckCircle2,  bg: '#8B5CF6', label: 'Quiz' },
  achievement: { icon: Trophy,        bg: '#F59E0B', label: 'Başarı' },
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return 'Az önce';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} saat önce`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) return 'Dün';
  if (diffDay < 7) return `${diffDay} gün önce`;

  // Tarih olarak göster
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

function formatFullDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface RecentActivityCardProps {
  items: ActivityLogItem[];
}

export function RecentActivityCard({ items }: RecentActivityCardProps) {
  return (
    <div className="nd-card">
      <div className="nd-card-header">
        <h2 className="nd-card-title">
          <span className="nd-card-title-icon">⚡</span>
          Son Aktiviteler
        </h2>
        {items.length > 0 && (
          <span className="nd-activity-week-badge">Son 7 gün</span>
        )}
      </div>

      <div className="nd-activity-list">
        {items.length === 0 ? (
          <div className="nd-empty-activity">
            <span className="nd-empty-activity-icon">📭</span>
            <p>Son 7 günde aktivite bulunamadı.</p>
            <Link to="/courses" className="nd-link-btn" style={{ fontSize: '0.8rem', padding: '7px 14px' }}>
              Derse Başla →
            </Link>
          </div>
        ) : (
          items.map((item) => {
            const meta = ACTIVITY_META[item.type] ?? ACTIVITY_META.lesson;
            const Icon = meta.icon;
            return (
              <div className="nd-activity-item" key={item.id} title={formatFullDate(item.occurredAt)}>
                <div className="nd-activity-icon" style={{ background: meta.bg }}>
                  <Icon size={16} color="#031D16" strokeWidth={2.5} />
                </div>
                <div className="nd-activity-content">
                  <span className="nd-activity-title">{item.title}</span>
                  <span className="nd-activity-desc">{item.description}</span>
                </div>
                <div className="nd-activity-right">
                  <span className="nd-activity-type-badge" style={{ background: `${meta.bg}22`, color: meta.bg }}>
                    {meta.label}
                  </span>
                  <span className="nd-activity-time">{formatDate(item.occurredAt)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Link to="/dashboard/report" className="nd-detail-btn">Tüm Aktiviteleri Gör →</Link>
    </div>
  );
}

